import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  completeNewPassword,
  confirmPasswordReset,
  getCurrentUser,
  requestPasswordReset,
  signInUser,
  signOut,
  updateCurrentUser,
} from '@/entities/user/user.api';
import { userQueryKeys } from '@/entities/user/user.keys';

export const useCurrentUserQuery = (options = {}) =>
  useQuery({
    queryKey: userQueryKeys.current,
    queryFn: getCurrentUser,
    retry: false,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useLogoutMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: signOut,
    onSuccess: async (...args) => {
      queryClient.setQueryData(userQueryKeys.current, null);
      await onSuccess?.(...args);
    },
    ...mutationOptions,
  });
};

export const useLoginMutation = (options = {}) =>
  useMutation({
    mutationFn: ({ email, password }) => signInUser(email, password),
    ...options,
  });

export const useCompleteNewPasswordMutation = (options = {}) =>
  useMutation({
    mutationFn: ({ user, password }) => completeNewPassword(user, password),
    ...options,
  });

export const useUpdateCurrentUserMutation = (options = {}) =>
  useMutation({
    mutationFn: ({ firstName, lastName }) =>
      updateCurrentUser({ firstName, lastName }),
    ...options,
  });

export const useRequestPasswordResetMutation = (options = {}) =>
  useMutation({
    mutationFn: ({ email }) => requestPasswordReset(email),
    ...options,
  });

export const useConfirmPasswordResetMutation = (options = {}) =>
  useMutation({
    mutationFn: ({ email, token, password }) =>
      confirmPasswordReset(email, token, password),
    ...options,
  });
